import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, AlertTriangle, Clock, CheckCircle2, 
  Calendar, FileText, ChevronRight, Target, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getAuditDashboard, FINDING_SEVERITIES, AUDIT_TYPES } from '@/services/audit';
import { toast } from 'sonner';

const AuditDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await getAuditDashboard();
      setDashboard(data);
    } catch (error) {
      toast.error('Failed to load audit dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const sev = FINDING_SEVERITIES.find(s => s.value === severity);
    return sev?.color || 'gray';
  };

  const getAuditTypeInfo = (type) => {
    return AUDIT_TYPES.find(t => t.value === type) || { label: type, icon: '📋' };
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of audit activities and findings</p>
        </div>
        <Link to="/grc/audit/engagements">
          <Button className="gap-2">
            <ClipboardCheck className="w-4 h-4" />
            View Engagements
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Engagements</p>
                <p className="text-3xl font-bold">{dashboard?.total_engagements || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ClipboardCheck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Findings</p>
                <p className="text-3xl font-bold">{dashboard?.total_findings || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue Findings</p>
                <p className="text-3xl font-bold text-red-600">{dashboard?.overdue_findings || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Audit Coverage</p>
                <p className="text-3xl font-bold text-green-600">{dashboard?.audit_coverage || 0}%</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Target className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Findings by Severity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Findings by Severity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {FINDING_SEVERITIES.map(sev => {
                const count = dashboard?.findings_by_severity?.[sev.value] || 0;
                const total = dashboard?.total_findings || 1;
                const percentage = (count / total * 100);
                return (
                  <div key={sev.value} className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full bg-${sev.color}-500`}></span>
                    <span className="text-sm text-gray-600 w-28">{sev.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div 
                        className={`bg-${sev.color}-500 h-2 rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Engagements by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Engagements by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {AUDIT_TYPES.slice(0, 6).map(type => {
                const count = dashboard?.engagements_by_type?.[type.value] || 0;
                return (
                  <div key={type.value} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{type.icon}</span>
                      <span className="text-sm font-medium">{type.label}</span>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Audits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Audits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.upcoming_audits || []).length > 0 ? (
                dashboard.upcoming_audits.map(audit => (
                  <Link 
                    key={audit.id}
                    to={`/grc/audit/engagements/${audit.id}`}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{audit.title}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(audit.planned_start_date).toLocaleDateString()} • {getAuditTypeInfo(audit.audit_type).label}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No upcoming audits scheduled</p>
              )}
            </div>
            <Link to="/grc/audit/schedule" className="block mt-4">
              <Button variant="outline" className="w-full">
                View Schedule <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Findings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Recent Findings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.recent_findings || []).length > 0 ? (
                dashboard.recent_findings.map(finding => (
                  <Link 
                    key={finding.id}
                    to={`/grc/audit/findings/${finding.id}`}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${
                      finding.severity === 'critical' ? 'bg-red-100' :
                      finding.severity === 'high' ? 'bg-orange-100' :
                      finding.severity === 'medium' ? 'bg-yellow-100' :
                      'bg-blue-100'
                    }`}>
                      <AlertTriangle className={`w-4 h-4 ${
                        finding.severity === 'critical' ? 'text-red-600' :
                        finding.severity === 'high' ? 'text-orange-600' :
                        finding.severity === 'medium' ? 'text-yellow-600' :
                        'text-blue-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{finding.title}</p>
                      <p className="text-xs text-gray-500">{finding.finding_id}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      finding.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      finding.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                      finding.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {finding.severity}
                    </span>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent findings</p>
              )}
            </div>
            <Link to="/grc/audit/findings" className="block mt-4">
              <Button variant="outline" className="w-full">
                View All Findings <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Link to="/grc/audit/schedule">
              <Button variant="outline" className="gap-2">
                <Calendar className="w-4 h-4" />
                Audit Schedule
              </Button>
            </Link>
            <Link to="/grc/audit/engagements">
              <Button variant="outline" className="gap-2">
                <ClipboardCheck className="w-4 h-4" />
                Engagements
              </Button>
            </Link>
            <Link to="/grc/audit/findings">
              <Button variant="outline" className="gap-2">
                <AlertTriangle className="w-4 h-4" />
                Findings
              </Button>
            </Link>
            <Link to="/grc/audit/workpapers">
              <Button variant="outline" className="gap-2">
                <FileText className="w-4 h-4" />
                Workpapers
              </Button>
            </Link>
            <Link to="/grc/audit/reports">
              <Button variant="outline" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Reports
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditDashboard;
